import type { ApiNotifyPeerType, ApiPeerNotifySettings } from '../../api/types';
import type {
  ISettings, IThemeSettings,
  ThemeKey,
} from '../../types';
import type { GlobalState } from '../types';

import { DEBUG } from '../../config';
import { logDebugMessage } from '../../util/debugConsole';
import {
  buildThemeWallpaperDebugSnapshot,
  hasThemeSettingsPatchChanged,
  hasThemeWallpaperMutation,
  hasThemeWallpaperSnapshotChanged,
} from '../../util/themeWallpaper';
import { updateUserBlockedState } from './users';

export function replaceSettings<T extends GlobalState>(global: T, newSettings?: Partial<ISettings>): T {
  return {
    ...global,
    settings: {
      ...global.settings,
      byKey: {
        ...global.settings.byKey,
        ...newSettings,
      },
    },
  };
}

export function replaceThemeSettings<T extends GlobalState>(
  global: T, theme: ThemeKey, newSettings?: Partial<IThemeSettings>,
): T {
  const previousThemeSettings = global.settings.themes[theme];
  if (!hasThemeSettingsPatchChanged(previousThemeSettings, newSettings)) {
    return global;
  }

  const nextThemeSettings = {
    ...(previousThemeSettings || {}),
    ...newSettings,
  };

  if (DEBUG && hasThemeWallpaperMutation(newSettings)
    && hasThemeWallpaperSnapshotChanged(previousThemeSettings, nextThemeSettings)) {
    logDebugMessage('debug', '[ThemeSettingsReducer] replaceThemeSettings', {
      theme,
      currentTheme: global.settings.byKey.theme,
      cloudThemeId: global.settings.byKey.cloudThemeId,
      patch: buildThemeWallpaperDebugSnapshot(newSettings),
      previous: buildThemeWallpaperDebugSnapshot(previousThemeSettings),
      next: buildThemeWallpaperDebugSnapshot(nextThemeSettings),
      sourceStack: buildDebugSourceStack(),
    });
  }

  return {
    ...global,
    settings: {
      ...global.settings,
      themes: {
        ...global.settings.themes,
        [theme]: nextThemeSettings,
      },
    },
  };
}

export function addNotifyExceptions<T extends GlobalState>(
  global: T, notifyExceptionById: Record<string, ApiPeerNotifySettings>,
): T {
  return {
    ...global,
    chats: {
      ...global.chats,
      notifyExceptionById: {
        ...global.chats.notifyExceptionById,
        ...notifyExceptionById,
      },
    },
  };
}

export function replaceNotifyExceptions<T extends GlobalState>(
  global: T, notifyExceptionById: Record<string, ApiPeerNotifySettings>,
): T {
  return {
    ...global,
    chats: {
      ...global.chats,
      notifyExceptionById,
    },
  };
}

export function addNotifyException<T extends GlobalState>(
  global: T, id: string, notifyException: ApiPeerNotifySettings,
): T {
  return {
    ...global,
    chats: {
      ...global.chats,
      notifyExceptionById: {
        ...global.chats.notifyExceptionById,
        [id]: notifyException,
      },
    },
  };
}

export function updateNotifyDefaults<T extends GlobalState>(
  global: T, peerType: ApiNotifyPeerType, settings: Partial<ApiPeerNotifySettings>,
): T {
  return {
    ...global,
    settings: {
      ...global.settings,
      notifyDefaults: {
        ...global.settings.notifyDefaults,
        [peerType]: {
          ...global.settings.notifyDefaults?.[peerType],
          ...settings,
        },
      },
    },
  };
}

export function addBlockedUser<T extends GlobalState>(global: T, contactId: string): T {
  global = updateUserBlockedState(global, contactId, true);

  return {
    ...global,
    blocked: {
      ...global.blocked,
      ids: [contactId, ...global.blocked.ids],
      totalCount: global.blocked.totalCount + 1,
    },
  };
}

export function removeBlockedUser<T extends GlobalState>(global: T, contactId: string): T {
  global = updateUserBlockedState(global, contactId, false);

  return {
    ...global,
    blocked: {
      ...global.blocked,
      ids: global.blocked.ids.filter((id) => id !== contactId),
      totalCount: global.blocked.totalCount - 1,
    },
  };
}

function buildDebugSourceStack() {
  const stack = new Error().stack
    ?.split('\n')
    .slice(2, 6)
    .map((line) => line.trim())
    .join(' <- ');

  return stack;
}
